import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePlayerSocket, type PlayerSocketEntity } from '@/hooks/use-player-socket';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { shouldRefetchQueriesOnLiveContextInvalidated } from '@/helpers/should-refetch-on-live-context-invalidated.helper';

/**
 * Keep Assets + Perform/Control in sync when playlist/slot cue caches invalidate.
 * Mounted on FlowDetail so it stays alive on Assets (which has no player socket of its own).
 * Joins both binding and origin rooms — BE often emits invalidate on origin only.
 */
export function useFlowDetailLiveContextSync({
  flow,
  eventSlotId,
}: {
  flow?: FlowDto | null;
  eventSlotId?: string | null;
}) {
  const queryClient = useQueryClient();
  const storeFlow = useFlowStore((s) => s.flow);
  const flowForRooms = flow ?? storeFlow;

  const planFlowId = flowForRooms
    ? flowForRooms.origin
      ? getObjectId(flowForRooms.origin)
      : flowForRooms.id
    : undefined;
  const bindingFlowId = flowForRooms ? resolveLiveBindingFlowId(flowForRooms, eventSlotId ?? undefined) : undefined;
  const liveIsRunning = Boolean(flowForRooms?.liveContext?.isRunning);

  const handler = useMemo(
    () => ({
      onPlayersInvalidated: () => undefined,
      onLiveContextInvalidated: (
        _entityId: string,
        _entityType: PlayerSocketEntity,
        meta?: { isRunning?: boolean },
      ) => {
        if (
          !shouldRefetchQueriesOnLiveContextInvalidated({
            liveIsRunning,
            isRunningFromEvent: meta?.isRunning,
          })
        ) {
          return;
        }
        // GET_FLOW first for Assets/playlist order; cue-sheet rebuild can be slow — keep it background.
        if (planFlowId) {
          void queryClient.refetchQueries({
            queryKey: [GET_FLOW_QUERY_KEY, planFlowId, eventSlotId ?? undefined],
          });
          void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, planFlowId] });
        } else {
          void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
        }
        void invalidateSimulationCueSheet(queryClient);
        void queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
        void queryClient.refetchQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
        void queryClient.refetchQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
      },
    }),
    [eventSlotId, liveIsRunning, planFlowId, queryClient],
  );

  usePlayerSocket(bindingFlowId ?? '', 'Flow', handler, Boolean(bindingFlowId));
  usePlayerSocket(planFlowId ?? '', 'Flow', handler, Boolean(planFlowId) && planFlowId !== bindingFlowId);
}
